import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Search, 
  Package, 
  Ship, 
  MapPin, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  ArrowRight,
  Calendar,
  Anchor
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { API_URL } from "../config/api";


export default function BLTrackingPage() {
  const [searchParams] = useSearchParams();
  const initialBL = searchParams.get("bl") || "";
  
  const [blNumber, setBlNumber] = useState(initialBL);
  const [loading, setLoading] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [error, setError] = useState(null);

  const handleSearch = async (e) => {
    e?.preventDefault();
    
    if (!blNumber.trim()) {
      setError("Please enter a BL number");
      return;
    }
    
    setLoading(true);
    setError(null);
    setTrackingData(null);
    
    try {
      const response = await fetch(
        `${API_URL}/lcl-status/public/tracking/${encodeURIComponent(blNumber.trim())}`
      );
      
      const data = await response.json();
      
      if (!data.found) {
        setError(data.message || "No shipment found with this BL number");
        setTrackingData(null);
      } else {
        setTrackingData(data);
        setError(null);
      }
    } catch (err) {
      setError("Failed to fetch tracking information. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "working":
        return "bg-green-100 text-green-700";
      case "pending":
        return "bg-orange-100 text-orange-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
      {/* Header */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-600 rounded-lg">
              <Ship className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Shipment Tracking</h1>
              <p className="text-slate-400 text-sm">Track your shipment by Bill of Lading number</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-6 mb-8"
        >
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Enter HBL or MBL number (e.g., MSCU1234567)"
                value={blNumber}
                onChange={(e) => setBlNumber(e.target.value.toUpperCase())}
                className="pl-10 h-12 text-lg"
                data-testid="bl-search-input"
              />
            </div>
            <Button 
              type="submit" 
              disabled={loading}
              className="h-12 px-8 bg-blue-600 hover:bg-blue-700"
              data-testid="bl-search-btn"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <Search className="h-5 w-5 mr-2" />
                  Track
                </>
              )}
            </Button>
          </form>
        </motion.div>

        {/* Error State */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="h-6 w-6 text-red-500 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-red-800">Shipment Not Found</h3>
                <p className="text-red-600 mt-1">{error}</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Tracking Results */}
        {trackingData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Status Card */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm">BL Number</p>
                    <h2 className="text-2xl font-bold font-mono mt-1">{trackingData.bl_number}</h2>
                  </div>
                  <div className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(trackingData.status)}`}>
                    {trackingData.status === "working" ? "In Process" : 
                     trackingData.status === "pending" ? "Pending Review" : "Unknown"}
                  </div>
                </div>
                {trackingData.pf_number && (
                  <p className="text-blue-200 text-sm mt-2">
                    Reference: {trackingData.pf_number}
                  </p>
                )}
              </div>

              {/* Current Status */}
              <div className="p-6 border-b">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-full">
                    {trackingData.status === "working" ? (
                      <CheckCircle className="h-6 w-6 text-blue-600" />
                    ) : (
                      <Clock className="h-6 w-6 text-orange-600" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Current Status</p>
                    <h3 className="text-lg font-semibold text-slate-800">
                      {trackingData.current_status?.description || "Processing"}
                    </h3>
                    {trackingData.current_status?.timestamp && (
                      <p className="text-sm text-slate-400 mt-0.5">
                        Last updated: {formatDate(trackingData.current_status.timestamp)}
                      </p>
                    )}
                  </div>
                </div>
                
                {trackingData.note && (
                  <div className="mt-4 p-3 bg-orange-50 rounded-lg">
                    <p className="text-sm text-orange-700">{trackingData.note}</p>
                  </div>
                )}
              </div>

              {/* Shipment Info */}
              <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase">Origin</p>
                  <p className="font-semibold text-slate-800 flex items-center gap-1 mt-1">
                    <Anchor className="h-4 w-4 text-slate-400" />
                    {trackingData.shipment_info?.origin_port || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Destination</p>
                  <p className="font-semibold text-slate-800 flex items-center gap-1 mt-1">
                    <MapPin className="h-4 w-4 text-slate-400" />
                    {trackingData.shipment_info?.destination_port || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Vessel</p>
                  <p className="font-semibold text-slate-800 flex items-center gap-1 mt-1">
                    <Ship className="h-4 w-4 text-slate-400" />
                    {trackingData.shipment_info?.vessel_name || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">ETA</p>
                  <p className="font-semibold text-slate-800 flex items-center gap-1 mt-1">
                    <Calendar className="h-4 w-4 text-slate-400" />
                    {formatDate(trackingData.shipment_info?.eta) || "-"}
                  </p>
                </div>
              </div>

              {/* Status History */}
              {trackingData.status_history && trackingData.status_history.length > 0 && (
                <div className="p-6 border-t bg-slate-50">
                  <h4 className="font-semibold text-slate-800 mb-4">Status History</h4>
                  <div className="space-y-3">
                    {trackingData.status_history.map((event, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className={`mt-1 w-2 h-2 rounded-full ${idx === 0 ? 'bg-blue-600' : 'bg-slate-300'}`} />
                        <div className="flex-1">
                          <p className={`font-medium ${idx === 0 ? 'text-slate-800' : 'text-slate-600'}`}>
                            {event.status}
                          </p>
                          <p className="text-xs text-slate-400">
                            {formatDate(event.timestamp)}
                            {event.location && ` • ${event.location}`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Contact Info */}
            <div className="bg-white rounded-xl p-6 text-center">
              <p className="text-slate-500">
                Need assistance? Contact us at{" "}
                <a href="mailto:info@europacific.com" className="text-blue-600 hover:underline">
                  info@europacific.com
                </a>
              </p>
            </div>
          </motion.div>
        )}

        {/* Initial State */}
        {!trackingData && !error && !loading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-12"
          >
            <Package className="h-16 w-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Enter your BL number to track</h3>
            <p className="text-slate-400 max-w-md mx-auto">
              Enter your House Bill of Lading (HBL) or Master Bill of Lading (MBL) number 
              to get real-time status updates on your shipment.
            </p>
          </motion.div>
        )}
      </div>

      {/* Footer */}
      <div className="max-w-4xl mx-auto px-4 py-8 text-center">
        <p className="text-slate-500 text-sm">
          Powered by myEuropacific Logistics
        </p>
      </div>
    </div>
  );
}
